import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import LatexRenderer from './LatexRenderer';
import { getDifficultyLevelUI } from '../utils/examDifficulty';

const parseQType = (qTypeId) => {
  const type = Number(qTypeId);
  switch (type) {
    case 1: return String.raw`$\textbf{MCQ}$`;
    case 2: return String.raw`$\textbf{Match the Following}$`;
    case 3: return String.raw`$\textbf{Assertion}$`;
    case 4: return String.raw`$\textbf{Reasoning}$`;
    case 5: return String.raw`$\textbf{Numerical}$`;
    case 6: return String.raw`$\textbf{Fill in the Blanks}$`;
    case 7: return String.raw`$\textbf{Image Based}$`;
    default: return String.raw`$\textbf{Unknown Type}$`;
  }
};

// Same rule the exam window scores by: a blank numerical/fill-in answer ("") is not an answer.
const isAnswered = (v) => v !== undefined && v !== null && v !== '';

function ResultsPage({ resultsData, onBackToDashboard }) {
  const [expandedId, setExpandedId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!resultsData) return <Box sx={{ p: '32px', textAlign: 'center' }}>Loading Results...</Box>;

  const { score, totalQuestions, correct, wrong, examType, evaluatedQuestions } = resultsData;
  // The exam window saves this count as `unattempted`; older results may carry `unanswered`.
  const unanswered = resultsData.unanswered ?? resultsData.unattempted;

  const toggleAccordion = (index) => {
    setExpandedId(expandedId === index ? null : index);
  };

  return (
    <Box>
      <Container maxWidth="md" disableGutters sx={{ mx: 0 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0px',
          mb: '32px'
        }}>
          <Typography sx={{ color: '#1d4ed8', fontWeight: 700, fontSize: '30px' }}>
            Test Results
          </Typography>
          <Button
            variant="outlined"
            onClick={onBackToDashboard}
            sx={{
              bgcolor: '#f1f5f9',
              color: '#475569',
              borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#e2e8f0', borderColor: '#cbd5e1' },
              fontWeight: 600,
              fontSize: '14px',
              px: '24px',
              textTransform: 'none'
            }}
          >
            Create Another Exam
          </Button>
        </Box>

        <Paper sx={{
          p: '28px',
          borderRadius: '16px',
          mb: '40px',
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: 'none'
        }}>
          <Grid container spacing={isMobile ? 2 : 0}>
            {[
              { label: 'Score', value: score, className: 'score', icon: 'solar:chart-2-bold' },
              { label: 'Total Questions', value: totalQuestions, subtext: examType, className: 'total', icon: 'solar:list-bold' },
              { label: 'Correct', value: correct, className: 'correct', icon: 'solar:check-circle-bold' },
              { label: 'Wrong', value: wrong, className: 'wrong', icon: 'solar:close-circle-bold' },
              { label: 'Unanswered', value: unanswered, className: 'unanswered', icon: 'solar:question-square-bold' }
            ].map((item, idx) => (
              <Grid size={{ xs: isMobile ? (idx === 0 ? 12 : 6) : 2.4 }} key={idx} sx={{
                textAlign: 'center',
                borderLeft: !isMobile && idx !== 0 ? '1px solid #f1f5f9' : 'none',
                px: '16px'
              }}>
                <Box sx={{ mb: '8px', color: item.className === 'score' ? '#1d4ed8' :
                                               item.className === 'correct' ? '#22c55e' :
                                               item.className === 'wrong' ? '#ef4444' : '#64748b' }}>
                  <Icon icon={item.icon} width="20" height="20" />
                </Box>
                <Typography sx={{
                  fontSize: '12px',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: '4px'
                }}>
                  {item.label}
                </Typography>
                <Typography sx={{
                  fontSize: item.className === 'total' ? '24px' : '32px',
                  fontWeight: 800,
                  color: item.className === 'score' ? '#1d4ed8' :
                    item.className === 'correct' ? '#16a34a' :
                      item.className === 'wrong' ? '#dc2626' :
                        item.className === 'unanswered' ? '#64748b' : '#334155',
                  lineHeight: 1
                }}>
                  {item.value}
                </Typography>
                {item.subtext && (
                  <Typography sx={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', mt: '4px', fontWeight: 600 }}>
                    {item.subtext}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Typography sx={{ fontSize: '20px', color: '#334155', mb: '20px', fontWeight: 600 }}>
          Questions Review
        </Typography>

        <Box>
          {!evaluatedQuestions && (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
              <Typography sx={{ color: '#64748b' }}>
                Detailed question review is not available for this past exam.
              </Typography>
            </Paper>
          )}

          {evaluatedQuestions && evaluatedQuestions
            .map((q, idx) => {
              const answered = isAnswered(q.studentAnswerIndex);
              let statusColor = '#94a3b8';
              let statusBg = '#f8fafc';
              if (q.isCorrect) {
                statusColor = '#22c55e';
                statusBg = '#f0fdf4';
              } else if (answered) {
                statusColor = '#ef4444';
                statusBg = '#fef2f2';
              }

              const isExpanded = expandedId === idx;

              return (
                <Accordion
                  key={idx}
                  expanded={isExpanded}
                  onChange={() => toggleAccordion(idx)}
                  sx={{
                    mb: '16px',
                    borderRadius: '10px !important',
                    boxShadow: 'none',
                    border: `1px solid ${isExpanded ? statusColor : '#e2e8f0'}`,
                    '&:before': { display: 'none' },
                    overflow: 'hidden'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Icon icon="iconamoon:arrow-down-2-light" width="22" height="22" />}
                    sx={{
                      bgcolor: isExpanded ? statusBg : 'white',
                      px: '24px',
                      '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        m: '12px 0'
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: '0.3s',
                        color: statusColor
                      }
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '16px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 'fit-content' }}>
                        <Box sx={{
                          bgcolor: statusColor,
                          color: '#ffffff',
                          minWidth: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: 800,
                          boxShadow: `0 4px 10px ${statusColor}30`
                        }}>
                          {idx + 1}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', mb: '-2px' }}>
                            Subject
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#334155', fontWeight: 700 }}>
                            <LatexRenderer text={q.subjectName} />
                          </Typography>
                        </Box>
                      </Box>

                      <Divider orientation="vertical" flexItem sx={{ display: isMobile ? 'none' : 'block', mx: '8px', height: '24px', alignSelf: 'center' }} />

                      <Typography sx={{ 
                        fontSize: '15px', 
                        color: '#334155', 
                        lineHeight: 1.5, 
                        fontWeight: 500,
                        display: '-webkit-box',
                        WebkitLineClamp: isExpanded ? 'unset' : '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flex: 1
                      }}>
                        <LatexRenderer text={q.parsedQuestionText || q.questionText || q.question} />
                      </Typography>

                      <Box sx={{ display: 'flex', gap: '8px', ml: isMobile ? '0px' : 'auto', mt: isMobile ? '8px' : '0px' }}>
                        {!answered && (
                          <Box sx={{ px: '10px', py: '4px', borderRadius: '6px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase' }}>
                            Not answered
                          </Box>
                        )}
                        <Box sx={{ px: '10px', py: '4px', borderRadius: '6px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', color: getDifficultyLevelUI(q.level_id || q.question?.level_id).color, fontSize: '10px', fontWeight: 800, textTransform: 'uppercase' }}>
                          {getDifficultyLevelUI(q.level_id || q.question?.level_id).label}
                        </Box>
                        <Box sx={{ px: '10px', py: '4px', borderRadius: '6px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', fontSize: '10px', fontWeight: 800 }}>
                          <LatexRenderer text={parseQType(q.qtype_id || q.question?.qtype_id)} />
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: '#fafafa', p: '24px', borderTop: `1px solid ${statusColor}30` }}>
                    {!(Number(q.qtype_id || q.question?.qtype_id) === 5 || Number(q.qtype_id || q.question?.qtype_id) === 6 || (q.options?.length === 1 && q.options[0] === '[Missing Options]')) && (
                      <>
                        <Typography sx={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', mb: '12px', fontWeight: 600 }}>
                          Options
                        </Typography>
                        <Box sx={{ mb: '32px' }}>
                          <Typography sx={{ fontSize: '13px', color: '#334155', fontWeight: 700, mb: '20px', display: 'block' }}>
                            <LatexRenderer text={q.parsedQuestionText || q.questionText || q.question} />
                          </Typography>
                          {q.options && q.options.map((opt, optIdx) => {
                        const isStudentChoice = answered && optIdx === q.studentAnswerIndex;
                        const isActualCorrect = (
                          String(q.correctAnswerValue) === String(optIdx + 1) ||
                          String(q.correctAnswerValue).toUpperCase() === String.fromCharCode(65 + optIdx) ||
                          String(q.correctAnswerValue) === String(opt) ||
                          String(q.correctAnswerValue).toLowerCase() === `option${optIdx + 1}` ||
                          (String(q.correctAnswerValue) === "0" && optIdx === 0)
                        );

                        let borderStyle = '1px solid #e2e8f0';
                        let bgStyle = '#ffffff';

                        if (isActualCorrect) {
                          borderStyle = '2px solid #22c55e';
                          bgStyle = '#f0fdf4';
                        } else if (isStudentChoice && !q.isCorrect) {
                          borderStyle = '2px solid #ef4444';
                          bgStyle = '#fef2f2';
                        }

                        return (
                          <Box key={optIdx} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: '14px 20px',
                            mb: '12px',
                            borderRadius: '10px',
                            border: borderStyle,
                            bgcolor: bgStyle,
                            transition: 'all 0.2s ease'
                          }}>
                            <Box sx={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              bgcolor: isActualCorrect ? '#22c55e' : (isStudentChoice && !q.isCorrect ? '#ef4444' : '#f1f5f9'),
                              color: isActualCorrect || (isStudentChoice && !q.isCorrect) ? '#fff' : '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 800,
                              mr: '16px',
                              flexShrink: 0
                            }}>
                              {String.fromCharCode(65 + optIdx)}
                            </Box>
                            <Typography sx={{ fontSize: '14px', color: '#334155', flex: 1, fontWeight: (isActualCorrect || isStudentChoice) ? 600 : 400 }}>
                              <LatexRenderer text={opt && typeof opt === 'object' ? opt.text : opt} />
                            </Typography>
                            {isActualCorrect && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#16a34a' }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase' }}>Correct</Typography>
                                <Icon icon="solar:check-circle-bold" width="20" height="20" />
                              </Box>
                            )}
                            {(isStudentChoice && !q.isCorrect) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase' }}>Your Choice</Typography>
                                <Icon icon="solar:close-circle-bold" width="20" height="20" />
                              </Box>
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                    </>
                    )}

                    <Grid container spacing={2} sx={{ mb: '24px' }}>
                      {[
                        { 
                          label: 'Your Answer', 
                          val: answered ? (typeof q.studentAnswerIndex === 'string' ? `$\\textbf{${q.studentAnswerIndex}}$` : `$\\textbf{${String.fromCharCode(65 + Number(q.studentAnswerIndex))}}$`) : "Not Answered", 
                          color: statusColor, 
                          icon: 'solar:user-bold' 
                        },
                        { 
                          label: 'Correct Answer', 
                          val: (Number(q.qtype_id || q.question?.qtype_id) === 5 || Number(q.qtype_id || q.question?.qtype_id) === 6 || (q.options?.length === 1 && q.options[0] === '[Missing Options]')) ? `$\\textbf{${q.correctAnswerValue || "Unknown"}}$` : (q.correctAnswerValue && !isNaN(q.correctAnswerValue) ? `$\\textbf{${String.fromCharCode(64 + Number(q.correctAnswerValue))}}$` : (q.correctAnswerValue || "Unknown")), 
                          color: '#16a34a', 
                          icon: 'solar:check-square-bold' 
                        },
                        { label: 'Marks', val: q.isCorrect ? '+4' : (answered ? '-1' : '0'), color: '#334155', icon: 'solar:medal-ribbons-star-bold' }
                      ].map((item, i) => (
                        <Grid size={{ xs: 12, sm: 4 }} key={i}>
                          <Box sx={{
                            p: '16px',
                            bgcolor: '#ffffff',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <Box sx={{ color: item.color, display: 'flex' }}>
                              <Icon icon={item.icon} width="20" height="20" />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {item.label}
                              </Typography>
                              <Typography sx={{ fontWeight: 800, color: item.color, fontSize: '15px' }}>
                                <LatexRenderer text={item.val} />
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={{
                      p: '20px',
                      bgcolor: '#eff6ff',
                      borderRadius: '12px',
                      border: '1px solid #dbeafe',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        bgcolor: '#3b82f6'
                      }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px' }}>
                        <Icon icon="solar:info-circle-bold" width="18" height="18" style={{ color: '#1d4ed8' }} />
                        <Typography sx={{ fontSize: '12px', color: '#1d4ed8', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.05em' }}>
                          Step-by-Step Explanation
                        </Typography>
                      </Box>
                      <Box sx={{ fontSize: '14px', color: '#1e40af', lineHeight: 1.7 }}>
                        {q.explanation || q.question?.explanation ? (
                          <LatexRenderer text={q.explanation || q.question?.explanation} />
                        ) : (
                          <Typography component="span" sx={{ fontStyle: 'italic', color: '#60a5fa', fontSize: '14px' }}>
                            No detailed explanation available for this question.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      </Container>
    </Box>
  );
}

export default ResultsPage;

